import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { 
  sendMessage, 
  getGroupMessages, 
  shareLocation, 
  getActiveLocations,
  getActivityUpdates,
  markMessageAsRead,
  getUnreadMessageCount,
  subscribeToMessages,
  subscribeToLocations,
  subscribeToActivityUpdates,
  unsubscribeFromChannel
} from '../../../services/messagingService';
import { useAuth } from '../../../contexts/AuthContext';

const GroupChatModal = ({ isOpen, onClose, groupId, groupName }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState('messages'); // messages, locations, activities
  const [locations, setLocations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const messageChannelRef = useRef(null);
  const locationChannelRef = useRef(null);
  const activityChannelRef = useRef(null);

  useEffect(() => {
    if (isOpen && groupId && user?.id) {
      loadInitialData();
      setupRealtimeSubscriptions();
    }

    return () => {
      cleanupSubscriptions();
    };
  }, [isOpen, groupId, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [messagesData, locationsData, activitiesData, unreadCountData] = await Promise.all([
        getGroupMessages(groupId),
        getActiveLocations(groupId),
        getActivityUpdates(groupId),
        getUnreadMessageCount(groupId, user?.id)
      ]);

      setMessages(messagesData?.reverse() || []);
      setLocations(locationsData || []);
      setActivities(activitiesData || []);
      setUnreadCount(unreadCountData || 0);

      // Mark messages as read
      messagesData?.forEach(msg => {
        if (msg?.senderId !== user?.id) {
          markMessageAsRead(msg?.id, user?.id);
        }
      });
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to messages
    messageChannelRef.current = subscribeToMessages(groupId, (message) => {
      setMessages(prev => [...prev, message]);
      if (message?.senderId !== user?.id) {
        markMessageAsRead(message?.id, user?.id);
      }
    });

    // Subscribe to locations
    locationChannelRef.current = subscribeToLocations(groupId, ({ type, location }) => {
      if (type === 'INSERT') {
        setLocations(prev => [...prev, location]);
      } else if (type === 'UPDATE') {
        setLocations(prev => prev?.map(loc => loc?.id === location?.id ? location : loc));
      }
    });

    // Subscribe to activities
    activityChannelRef.current = subscribeToActivityUpdates(groupId, (activity) => {
      setActivities(prev => [activity, ...prev]);
    });
  };

  const cleanupSubscriptions = () => {
    if (messageChannelRef?.current) {
      unsubscribeFromChannel(messageChannelRef?.current);
    }
    if (locationChannelRef?.current) {
      unsubscribeFromChannel(locationChannelRef?.current);
    }
    if (activityChannelRef?.current) {
      unsubscribeFromChannel(activityChannelRef?.current);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || isSending) return;

    setIsSending(true);
    try {
      const result = await sendMessage(groupId, user?.id, newMessage?.trim());
      if (result?.success) {
        setNewMessage('');
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message.');
    } finally {
      setIsSending(false);
    }
  };

  const handleShareLocation = async () => {
    if (!navigator?.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        try {
          const result = await shareLocation(
            groupId,
            user?.id,
            position?.coords?.latitude,
            position?.coords?.longitude,
            'Current Location'
          );

          if (result?.success) {
            alert('Location shared successfully!');
          } else {
            alert('Failed to share location. Please try again.');
          }
        } catch (error) {
          console.error('Error sharing location:', error);
          alert('Failed to share location.');
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please enable location services.');
        setIsLoading(false);
      }
    );
  };

  const getMessageIcon = (messageType) => {
    switch (messageType) {
      case 'location':
        return 'MapPin';
      case 'activity':
        return 'Activity';
      case 'system':
        return 'Info';
      default:
        return 'MessageCircle';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-card border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon name="MessageCircle" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{groupName}</h2>
              <p className="text-xs text-muted-foreground">Group Chat</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border flex">
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'messages' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="MessageCircle" size={16} className="inline mr-2" />
            Messages
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-destructive text-destructive-foreground text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('locations')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'locations' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="MapPin" size={16} className="inline mr-2" />
            Locations ({locations?.length})
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'activities' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Activity" size={16} className="inline mr-2" />
            Activities ({activities?.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : activeTab === 'messages' ? (
            <div className="space-y-4">
              {messages?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="MessageCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages?.map((message) => (
                  <div
                    key={message?.id}
                    className={`flex ${
                      message?.senderId === user?.id ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        message?.senderId === user?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {message?.messageType !== 'text' && (
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name={getMessageIcon(message?.messageType)} size={14} />
                          <span className="text-xs opacity-80">
                            {message?.messageType === 'location' ? 'Location' : 'Activity'}
                          </span>
                        </div>
                      )}
                      <p className="text-sm">{message?.content}</p>
                      <p className="text-xs opacity-70 mt-1">{formatTime(message?.createdAt)}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          ) : activeTab === 'locations' ? (
            <div className="space-y-3">
              {locations?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No locations shared yet</p>
                </div>
              ) : (
                locations?.map((location) => (
                  <div
                    key={location?.id}
                    className="p-4 bg-muted/50 rounded-lg border border-border"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                          <Icon name="MapPin" size={18} className="text-accent" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {location?.locationName || 'Shared Location'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {location?.latitude?.toFixed(6)}, {location?.longitude?.toFixed(6)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(location?.sharedAt)}
                          </p>
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${location?.latitude},${location?.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        View on Map
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {activities?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No activity updates yet</p>
                </div>
              ) : (
                activities?.map((activity) => (
                  <div
                    key={activity?.id}
                    className="p-4 bg-muted/50 rounded-lg border border-border"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Icon name="Activity" size={18} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{activity?.title}</p>
                        {activity?.description && (
                          <p className="text-sm text-muted-foreground mt-1">{activity?.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {formatDate(activity?.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Message Input (only show on messages tab) */}
        {activeTab === 'messages' && (
          <div className="border-t border-border p-4">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <button
                type="button"
                onClick={handleShareLocation}
                disabled={isLoading}
                className="w-10 h-10 rounded-lg bg-accent/20 hover:bg-accent/30 flex items-center justify-center transition-colors"
                aria-label="Share location"
              >
                <Icon name="MapPin" size={18} className="text-accent" />
              </button>
              <Input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder="Type a message..."
                className="flex-1"
                disabled={isSending}
              />
              <Button
                type="submit"
                disabled={!newMessage?.trim() || isSending}
                className="w-10 h-10 p-0"
              >
                <Icon name="Send" size={18} />
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupChatModal;